import React, { useState } from "react";
import { motion } from "framer-motion";
import {
  FaFacebookF,
  FaTwitter,
  FaInstagram,
  FaLinkedinIn,
} from "react-icons/fa";
import axios from "axios";

export default function ContactUs() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "", // ✅ Added phone
    message: "",
  });

  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitted(false);

    try {
      const res = await axios.post(
        "https://accessoriesgirly.onrender.com/api/inquiries",
        formData
      );
      if (res.status === 201) {
        setSubmitted(true);
        setFormData({ name: "", email: "", phone: "", message: "" });
      }
    } catch (err) {
      console.error("Error submitting inquiry:", err);
      setError("Failed to send message. Please try again later.");
    }
  };

  const containerVariant = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: "easeOut" },
    },
  };

  const inputVariant = {
    hidden: { opacity: 0, x: -20 },
    visible: {
      opacity: 1,
      x: 0,
      transition: { duration: 0.5, ease: "easeOut" },
    },
  };

  const contentVariant = {
    hidden: { opacity: 0, x: -50 },
    visible: {
      opacity: 1,
      x: 0,
      transition: { duration: 0.7, ease: "easeOut" },
    },
  };

  const iconVariant = {
    hidden: { opacity: 0, y: 20 },
    visible: (i) => ({
      opacity: 1,
      y: 0,
      transition: { delay: i * 0.2, duration: 0.5, ease: "easeOut" },
    }),
  };

  return (
    <section className="py-16 bg-pink-50 min-h-screen">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Heading */}
        <motion.h2
          className="text-4xl font-bold text-pink-600 text-center mb-8"
          variants={containerVariant}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          Contact Us
        </motion.h2>

        <motion.p
          className="text-center text-gray-700 mb-12"
          variants={containerVariant}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          We’d love to hear from you! Reach out with any questions, suggestions,
          or just to say hello.
        </motion.p>

        {/* Two-column layout */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-12">
          {/* Left: Contact Info */}
          <motion.div
            className="space-y-6"
            variants={contentVariant}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            <h3 className="text-2xl font-semibold text-pink-600">
              Get in Touch
            </h3>
            <p className="text-gray-700">
              You can reach us via the contact form or visit our office. We’re
              available Monday to Friday from 9:00 AM to 6:00 PM. Your feedback
              and questions are valuable to us, so don’t hesitate to get in
              touch!
            </p>

            <div className="space-y-2 text-gray-800">
              <p>
                <strong>Address:</strong> Sirsa,Haryana
              </p>
              <p>
                <strong>Email:</strong> accessoriesgirly701@gmail.com
              </p>
              <p>
                <strong>Phone:</strong> +91 93062-66352
              </p>
            </div>
          </motion.div>

          {/* Right: Contact Form */}
          <motion.form
            onSubmit={handleSubmit}
            className="bg-white shadow-lg rounded-2xl p-8 flex flex-col gap-6"
            variants={contentVariant}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            {submitted && (
              <motion.div
                className="p-4 text-green-700 bg-green-100 rounded-lg text-center"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5 }}
              >
                Your message has been sent successfully!
              </motion.div>
            )}

            {/* Name */}
            <motion.input
              type="text"
              name="name"
              placeholder="Your Name"
              value={formData.name}
              onChange={handleChange}
              className="px-4 py-3 border border-pink-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500"
              variants={inputVariant}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
            />

            {/* Email */}
            <motion.input
              type="email"
              name="email"
              placeholder="Your Email"
              value={formData.email}
              onChange={handleChange}
              className="px-4 py-3 border border-pink-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500"
              variants={inputVariant}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
            />

            {/* ✅ Phone */}
            <motion.input
              type="tel"
              name="phone"
              placeholder="Your Phone Number"
              value={formData.phone}
              onChange={handleChange}
              className="px-4 py-3 border border-pink-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500"
              variants={inputVariant}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
            />

            {/* Message */}
            <motion.textarea
              name="message"
              placeholder="Your Message"
              value={formData.message}
              onChange={handleChange}
              rows="5"
              className="px-4 py-3 border border-pink-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 resize-none"
              variants={inputVariant}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
            />

            {/* Submit */}
            <motion.button
              type="submit"
              className="bg-pink-600 text-white px-6 py-3 rounded-lg hover:bg-pink-700 transition-colors"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Send Message
            </motion.button>

            {error && (
              <motion.div
                className="p-4 text-red-700 bg-red-100 rounded-lg text-center"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5 }}
              >
                {error}
              </motion.div>
            )}
          </motion.form>
        </div>

        {/* Map */}
        <motion.div
          className="rounded-xl overflow-hidden shadow-lg mb-12"
          variants={containerVariant}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          <iframe
            title="Office Location"
            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3466.247653721691!2d75.0166861848954!3d29.53260498196019!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3910041b57e90b2d%3A0x4c2f234b01234567!2sSirsa%2C+Haryana%2C+India!5e0!3m2!1sen!2sin!4v1694700000000!5m2!1sen!2sin"
            width="100%"
            height="400"
            style={{ border: 0 }}
            allowFullScreen=""
            loading="lazy"
          ></iframe>
          <a
            href="https://www.google.com/maps/dir/?api=1&destination=Sirsa,+Haryana,+India"
            target="_blank"
            rel="noopener noreferrer"
            className="block text-center bg-pink-600 text-white px-4 py-3 hover:bg-pink-700 transition-colors"
          >
            Get Directions
          </a>
        </motion.div>

        {/* Social Media */}
        <motion.div
          className="text-center space-y-4"
          variants={containerVariant}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          <p className="text-gray-700 text-lg">
            Follow us on social media for updates and news:
          </p>
          <div className="flex justify-center gap-6 text-pink-600 text-xl">
            {[FaFacebookF, FaTwitter, FaInstagram, FaLinkedinIn].map(
              (Icon, i) => (
                <motion.a
                  key={i}
                  href="#"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:text-pink-800"
                  custom={i}
                  variants={iconVariant}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                >
                  <Icon />
                </motion.a>
              )
            )}
          </div>
        </motion.div>
      </div>
    </section>
  );
}
